import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AiFillEye, AiFillGithub, AiOutlineArrowLeft } from 'react-icons/ai';
import { SiEthereum } from 'react-icons/si';
import { motion } from 'framer-motion';

import { urlFor, client } from '../../client';
import { useTheme } from '../../context/ThemeContext';
import './ProjectDetails.scss';

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setError(null);

    // Try to fetch blockchain project details first, then fallback to works
    const blockchainQuery = `*[_type == "blockchainProjectDetails" && _id == "${projectId}"]`;
    const worksQuery = `*[_type == "works" && _id == "${projectId}"]`;

    console.log('Fetching blockchain project with ID:', projectId);
    console.log('Query:', blockchainQuery);

    client
      .fetch(blockchainQuery)
      .then((data) => {
        console.log('Blockchain query result:', data);
        if (data && data.length > 0) {
          console.log('Found blockchain project:', data[0]);
          setProject({ ...data[0], isBlockchain: true });
          setLoading(false);
          return Promise.resolve(null); // Stop chain if found
        } else {
          console.log('No blockchain project, trying works...');
          // Try works collection if blockchain project not found
          return client.fetch(worksQuery);
        }
      })
      .then((data) => {
        if (data === null) return; // Stop if blockchain project was found
        
        console.log('Works query result:', data);
        if (data && data.length > 0) {
          console.log('Found works project:', data[0]);
          setProject({ ...data[0], isBlockchain: false });
          setLoading(false);
        } else {
          console.log('No project found');
          setError('Project not found');
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Error fetching project:', err);
        setError('Error fetching project details');
        setLoading(false);
      });
  }, [projectId]);

  if (loading) {
    return (
      <div className={`project-details ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="project-details-container">
          <div className="loading">Loading project details...</div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className={`project-details ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="project-details-container">
          <motion.button
            onClick={() => navigate('/work')}
            className="back-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <AiOutlineArrowLeft /> Back to Works
          </motion.button>
          <div className="error">{error || 'Project not found'}</div>
        </div>
      </div>
    );
  }

  const isBlockchain = project.isBlockchain;

  return (
    <div className={`project-details ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="project-details-container">
        <motion.button
          onClick={() => navigate('/work')}
          className="back-button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <AiOutlineArrowLeft /> Back to Works
        </motion.button>

        {/* ===== BLOCKCHAIN PROJECT LAYOUT ===== */}
        {isBlockchain ? (
          <motion.div
            className="blockchain-project-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Hero Section */}
            <motion.div
              className="hero-section"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="hero-grid">
                <div className="hero-image-wrapper">
                  {project.heroImage && (
                    <img
                      src={urlFor(project.heroImage)}
                      alt={project.title}
                      className="hero-image"
                    />
                  )}
                </div>
                <div className="hero-info">
                  <div className="category-badge">{project.category?.toUpperCase()}</div>
                  <h1 className="hero-title">{project.title}</h1>
                  <p className="hero-pitch">{project.shortPitch}</p>

                  {/* Action Buttons */}
                  <div className="hero-actions">
                    {project.liveLink && (
                      <motion.a
                        href={project.liveLink}
                        target="_blank"
                        rel="noreferrer"
                        className="action-button live-button"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <AiFillEye /> View Live DApp
                      </motion.a>
                    )}
                    {project.githubLink && (
                      <motion.a
                        href={project.githubLink}
                        target="_blank"
                        rel="noreferrer"
                        className="action-button github-button"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <AiFillGithub /> GitHub Repo
                      </motion.a>
                    )}
                    {project.smartContractLink && (
                      <motion.a
                        href={project.smartContractLink}
                        target="_blank"
                        rel="noreferrer"
                        className="action-button contract-button"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <SiEthereum /> Smart Contract
                      </motion.a>
                    )}
                    {project.demoVideoLink && (
                      <motion.a
                        href={project.demoVideoLink}
                        target="_blank"
                        rel="noreferrer"
                        className="action-button demo-button"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        ▶ View Demo Video
                      </motion.a>
                    )}
                  </div>
                </div>
              </div>

              {/* Mobile Preview */}
              {project.mobilePreview && (
                <motion.div
                  className="mobile-preview-section"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <h3>Mobile Experience</h3>
                  <img
                    src={urlFor(project.mobilePreview)}
                    alt="Mobile Preview"
                    className="mobile-preview-image"
                  />
                </motion.div>
              )}
            </motion.div>

            {/* Tabs Navigation */}
            <motion.div
              className="tabs-navigation"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <button
                className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button
                className={`tab-button ${activeTab === 'features' ? 'active' : ''}`}
                onClick={() => setActiveTab('features')}
              >
                Features
              </button>
              <button
                className={`tab-button ${activeTab === 'technical' ? 'active' : ''}`}
                onClick={() => setActiveTab('technical')}
              >
                Technical Deep Dive
              </button>
              <button
                className={`tab-button ${activeTab === 'ux' ? 'active' : ''}`}
                onClick={() => setActiveTab('ux')}
              >
                UX & Testing
              </button>
            </motion.div>

            {/* Tab Content */}
            <motion.div
              className="tab-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              key={activeTab}
            >
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <motion.div
                  className="tab-pane overview-pane"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {/* Tech Stack */}
                  {project.techStack && (
                    <div className="tech-stack-section">
                      <h2>Tech Stack</h2>
                      <div className="tech-grid">
                        {project.techStack.frontend && project.techStack.frontend.length > 0 && (
                          <div className="tech-category">
                            <h4>Frontend</h4>
                            <div className="tech-tags">
                              {project.techStack.frontend.map((tech, idx) => (
                                <span key={idx} className="tech-tag frontend-tag">
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {project.techStack.blockchain && project.techStack.blockchain.length > 0 && (
                          <div className="tech-category">
                            <h4>Blockchain/Web3</h4>
                            <div className="tech-tags">
                              {project.techStack.blockchain.map((tech, idx) => (
                                <span key={idx} className="tech-tag blockchain-tag">
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {project.techStack.backend && project.techStack.backend.length > 0 && (
                          <div className="tech-category">
                            <h4>Backend/Indexing</h4>
                            <div className="tech-tags">
                              {project.techStack.backend.map((tech, idx) => (
                                <span key={idx} className="tech-tag backend-tag">
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {project.techStack.other && project.techStack.other.length > 0 && (
                          <div className="tech-category">
                            <h4>Other Tools</h4>
                            <div className="tech-tags">
                              {project.techStack.other.map((tech, idx) => (
                                <span key={idx} className="tech-tag other-tag">
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  {project.description && (
                    <div className="description-section">
                      <h2>About This Project</h2>
                      <p>{project.description}</p>
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="metadata-section">
                    <div className="metadata-grid">
                      {project.completedDate && (
                        <div className="metadata-item">
                          <span className="metadata-label">Completed</span>
                          <span className="metadata-value">
                            {new Date(project.completedDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {project.teamSize && (
                        <div className="metadata-item">
                          <span className="metadata-label">Team Size</span>
                          <span className="metadata-value">{project.teamSize}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Features Tab */}
              {activeTab === 'features' && (
                <motion.div
                  className="tab-pane features-pane"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <h2>Key Features & Functionality</h2>
                  <div className="features-list">
                    {project.keyFeatures && project.keyFeatures.map((feature, idx) => (
                      <motion.div
                        key={idx}
                        className="feature-card"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <div className="feature-number">{idx + 1}</div>
                        <div className="feature-content">
                          <h4>{feature.title}</h4>
                          <p>{feature.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Technical Deep Dive Tab */}
              {activeTab === 'technical' && (
                <motion.div
                  className="tab-pane technical-pane"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {/* Smart Contract Architecture */}
                  {project.smartContractArchitecture && (
                    <div className="technical-subsection">
                      <h3>📋 Smart Contract Architecture</h3>
                      <p>{project.smartContractArchitecture.overview}</p>
                      {project.smartContractArchitecture.standardsUsed && (
                        <div className="standards-used">
                          <h4>Standards Used:</h4>
                          <div className="standards-grid">
                            {project.smartContractArchitecture.standardsUsed.map((std, idx) => (
                              <span key={idx} className="standard-badge">
                                {std}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {project.smartContractArchitecture.architectureDiagram && (
                        <div className="architecture-diagram-wrapper">
                          <h4>Architecture Diagram</h4>
                          <img
                            src={urlFor(project.smartContractArchitecture.architectureDiagram)}
                            alt="Architecture Diagram"
                            className="architecture-diagram"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Challenges & Solutions */}
                  {project.challengesSolutions && project.challengesSolutions.length > 0 && (
                    <div className="technical-subsection">
                      <h3>🎯 Challenges & Solutions</h3>
                      <div className="challenges-list">
                        {project.challengesSolutions.map((item, idx) => (
                          <motion.div
                            key={idx}
                            className="challenge-item"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: idx * 0.1 }}
                          >
                            <div className="challenge-box">
                              <h4>❌ Challenge</h4>
                              <p>{item.challenge}</p>
                            </div>
                            <div className="solution-box">
                              <h4>✅ Solution</h4>
                              <p>{item.solution}</p>
                            </div>
                            <div className="impact-box">
                              <h4>📈 Impact</h4>
                              <p>{item.impact}</p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* UX & Testing Tab */}
              {activeTab === 'ux' && (
                <motion.div
                  className="tab-pane ux-pane"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {/* Testing & Security */}
                  {project.testingAndSecurity && (
                    <div className="testing-section">
                      <h3>🔒 Testing & Security</h3>
                      {project.testingAndSecurity.testCoverage && (
                        <div className="info-box">
                          <h4>Test Coverage</h4>
                          <p>{project.testingAndSecurity.testCoverage}</p>
                        </div>
                      )}
                      {project.testingAndSecurity.testingApproach && (
                        <div className="info-box">
                          <h4>Testing Approach</h4>
                          <p>{project.testingAndSecurity.testingApproach}</p>
                        </div>
                      )}
                      {project.testingAndSecurity.securityMeasures && (
                        <div className="info-box">
                          <h4>Security Measures</h4>
                          <ul className="security-list">
                            {project.testingAndSecurity.securityMeasures.map((measure, idx) => (
                              <li key={idx}>{measure}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {project.testingAndSecurity.auditInfo && (
                        <div className="info-box audit-box">
                          <h4>Audit Information</h4>
                          <p>{project.testingAndSecurity.auditInfo}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* User Experience */}
                  {project.userExperience && (
                    <div className="ux-section">
                      <h3>🎨 User Experience Highlights</h3>
                      {project.userExperience.transactionStates && (
                        <div className="info-box">
                          <h4>Transaction State Handling</h4>
                          <p>{project.userExperience.transactionStates}</p>
                        </div>
                      )}
                      {project.userExperience.loading && (
                        <div className="info-box">
                          <h4>⏳ Loading States</h4>
                          <p>{project.userExperience.loading}</p>
                        </div>
                      )}
                      {project.userExperience.notifications && (
                        <div className="info-box">
                          <h4>🔔 Notification Strategy</h4>
                          <p>{project.userExperience.notifications}</p>
                        </div>
                      )}
                      {project.userExperience.realTimeUpdates && (
                        <div className="info-box">
                          <h4>⚡ Real-time Updates</h4>
                          <p>{project.userExperience.realTimeUpdates}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Lessons Learned */}
                  {project.lessonsLearned && (
                    <div className="lessons-section">
                      <h3>📚 Lessons Learned</h3>
                      <div className="lessons-box">
                        <p>{project.lessonsLearned}</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        ) : (
          /* ===== STANDARD PROJECT LAYOUT (Legacy) ===== */
          <motion.div
            className="project-details-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="project-image-section">
              <motion.img
                src={urlFor(project.imgUrl)}
                alt={project.title}
                className="project-main-image"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              />
            </div>

            <div className="project-info-section">
              <motion.h1
                className="project-title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                {project.title}
              </motion.h1>

              <motion.div
                className="project-tags"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                {project.tags &&
                  project.tags.map((tag, index) => (
                    <span key={index} className="tag">
                      {tag}
                    </span>
                  ))}
              </motion.div>

              <motion.p
                className="project-description"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                {project.description}
              </motion.p>

              <motion.div
                className="project-links"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <motion.a
                  href={project.projectLink}
                  target="_blank"
                  rel="noreferrer"
                  className="link-button view-link"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <AiFillEye /> View Live
                </motion.a>
                <motion.a
                  href={project.codeLink}
                  target="_blank"
                  rel="noreferrer"
                  className="link-button code-link"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <AiFillGithub /> View Code
                </motion.a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetails;
